import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bot, Save, Sparkles, RefreshCw, CheckCircle, AlertCircle,
  MessageSquare, ChevronDown, ChevronUp, Settings, GraduationCap, Mic,
} from 'lucide-react';
import {
  getExperiences,
  getExperienceById,
  getPersonalities,
  getVoices,
  updateExperience,
  getInteractionsByExperience,
  upsertGeminiFallbackForExperience,
  getGeminiFallbackForExperience,
} from '../services/legacyApiService';
import { LegacyExperience, LegacyInteraction, LegacyPersonality, LegacyVoice } from '../types/legacy';
import JungArchetypeWheel, { resolveJungArchetype } from '../components/JungArchetypeWheel';
import {
  resolveExperienceCoverUrl,
  resolveAvatarUrl,
} from '../utils/imageUtils';
import {
  buildDefaultFinalMessage,
  buildDefaultGeniusLociName,
  buildDefaultIntermediateMessage,
  computeExperienceFlowStatus,
  extractKnowledgeContext,
  isExperienceGeniusConfigured,
} from '../utils/experienceFlow';
import { ApeConfig, getApeConfig } from '../services/apeConfigService';
import {
  fromLegacyVoice,
  resolveLegacyVoiceCode,
} from '../services/voiceProfiles';
import VoiceCatalogPicker from '../components/VoiceCatalogPicker';
import {
  getExperienceGeniusOverride,
  saveExperienceGeniusOverride,
  type ExperienceGeniusOverride,
} from '../services/experienceGeniusOverrideService';

interface PromptForm {
  nomeChatbot: string;
  messIntermedio: string;
  messFinale: string;
  voce: number | '';
  idPersonality: number | '';
  knowledgeContext: string;
  geminiSystemPrompt: string;
}

interface PromptsGeniusLociProps {
  embedded?: boolean;
  embeddedExperienceId?: number | null;
  onExperienceUpdated?: (experience: LegacyExperience) => void;
  onExperienceDraftChange?: (experience: Partial<LegacyExperience>) => void;
}

const emptyPrompt: PromptForm = {
  nomeChatbot: '',
  messIntermedio: '',
  messFinale: '',
  voce: '',
  idPersonality: '',
  knowledgeContext: '',
  geminiSystemPrompt: '',
};

const APE_SCHEMA_START = '[APE_SCHEMA_START]';
const APE_SCHEMA_END = '[APE_SCHEMA_END]';
const LEGACY_PROMPT_MAX_LENGTH = 3900;

const EMPTY_APE_CONFIG: ApeConfig = {
  level0Prompt: '',
  level6Prompt: '',
  generationPromptTemplate: '',
  level0Enabled: false,
  level6Enabled: false,
};

function normalizeLooseText(value: string | null | undefined): string {
  return String(value || '').trim();
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripApeSchema(prompt: string): string {
  const normalized = normalizeLooseText(prompt);
  if (!normalized) return '';
  if (!normalized.includes(APE_SCHEMA_START) || !normalized.includes(APE_SCHEMA_END)) return normalized;
  const pattern = new RegExp(`${escapeRegex(APE_SCHEMA_START)}[\\s\\S]*?${escapeRegex(APE_SCHEMA_END)}\\n*`, 'g');
  return normalized.replace(pattern, '').trim();
}

function buildApeSchemaBlock(params: {
  experience: LegacyExperience;
  intents: LegacyInteraction[];
  knowledgeContext: string;
  jungPersonality: string;
  selectedVoice?: LegacyVoice | null;
  apeConfig?: ApeConfig;
}): string {
  const {
    experience,
    intents,
    knowledgeContext,
    jungPersonality,
    selectedVoice,
    apeConfig,
  } = params;
  const voiceCode = resolveLegacyVoiceCode(selectedVoice);
  const voiceLabel = selectedVoice
    ? `${selectedVoice.name}${voiceCode ? ` (${voiceCode})` : ''}`
    : (experience.voce ? `Voce legacy #${experience.voce}` : 'non configurata');
  const interactionStyle = normalizeLooseText(experience.messIntermedio)
    ? 'accompagnamento narrativo progressivo durante il percorso'
    : 'risposte concise e contestuali';
  const constraints = normalizeLooseText(knowledgeContext)
    ? 'attenersi ai contenuti verificati della base di conoscenza'
    : 'base conoscenza vuota: usare solo esperienza+intent senza inventare dati';
  const hasLevel0 = Boolean(String(apeConfig?.level0Prompt || '').trim());
  const hasLevel6 = Boolean(String(apeConfig?.level6Prompt || '').trim());

  return [
    APE_SCHEMA_START,
    'Schema APE L0-L6 (allineato automaticamente):',
    `L0 Regole globali: ${hasLevel0 ? 'attive da configurazione centrale' : 'non configurate (default piattaforma)'}`,
    `L1 Contesto esperienza: nome="${normalizeLooseText(experience.name) || 'non definito'}"; citta="${normalizeLooseText(experience.city) || 'non definita'}"`,
    `L2 Identita: nome="${experience.nomeChatbot || 'Genius Loci'}"; archetipo_jung="${jungPersonality || 'Neutro'}"; personaggio="${normalizeLooseText(experience.tutorDescription) || 'Guida culturale del territorio'}"`,
    `L3 Voce e tono: voice_it="${voiceLabel}"; registro="${normalizeLooseText(experience.tutorLanguageRegister) || 'coinvolgente e culturale'}"; lingua="${normalizeLooseText(experience.language) || 'it-IT'}"`,
    `L4 Interazione: stile="${interactionStyle}"; risposta_target="2-5 frasi, chiare, con chiusura utile"; intent_disponibili=${intents.length}`,
    `L5 Vincoli: contesto="${normalizeLooseText(experience.name)}${experience.city ? ` - ${normalizeLooseText(experience.city)}` : ''}"; regole="${constraints}"`,
    `L6 Adattamento utente: ${hasLevel6 ? 'attivo da configurazione centrale (runtime mobile/web)' : 'template non configurato (fallback runtime)'}`,
    APE_SCHEMA_END,
  ].join('\n');
}

function composePromptWithApe(params: {
  basePrompt: string;
  experience: LegacyExperience;
  intents: LegacyInteraction[];
  knowledgeContext: string;
  jungPersonality: string;
  selectedVoice?: LegacyVoice | null;
  apeConfig?: ApeConfig;
}): string {
  const body = stripApeSchema(params.basePrompt);
  const apeBlock = buildApeSchemaBlock({
    experience: params.experience,
    intents: params.intents,
    knowledgeContext: params.knowledgeContext,
    jungPersonality: params.jungPersonality,
    selectedVoice: params.selectedVoice,
    apeConfig: params.apeConfig,
  });
  return `${apeBlock}\n\n${body}`.trim();
}

function buildGeminiSystemPrompt(
  exp: LegacyExperience,
  intents: LegacyInteraction[],
  knowledgeContext?: string,
  resolvedJungPersonality?: string,
): string {
  const cleanKnowledge = extractKnowledgeContext(knowledgeContext);
  const characterName = String(exp.nomeChatbot || 'Guida del territorio').trim();
  const roleOrStory = String(
    exp.tutorDescription
    || resolvedJungPersonality
    || exp.tutorPersonality
    || 'figura narrante storica legata al territorio',
  ).trim();
  const tone = String(
    exp.tutorLanguageRegister
    || 'coinvolgente, immersivo, coerente con il personaggio',
  ).trim();
  const place = String(
    exp.city
    || exp.name
    || 'questo territorio',
  ).trim();
  const historicalEpoch = String(exp.tutorEpoch || '').trim();
  const intentLabels = intents
    .slice(0, 25)
    .map((item) => String(item.displayName || '').trim())
    .filter(Boolean);
  const effectiveJungPersonality = String(
    resolvedJungPersonality
    || exp.tutorPersonality
    || '',
  ).trim();

  return `Impersona "${characterName}", ${roleOrStory}.

Parla sempre in prima persona, con tono coerente al tuo carattere: ${tone}.

Sei una guida turistica completa del territorio di ${place}.

---

UTILIZZO DELLA RICERCA ONLINE (GOOGLE GROUNDING)

Quando necessario:
- Effettua ricerche online per ottenere informazioni aggiornate, precise e rilevanti.
- Integra le informazioni nel racconto in modo naturale.
- Rielabora sempre i contenuti, senza copiarli.
- Non citare mai esplicitamente fonti o link.

---

AMBITO DI CONOSCENZA

Puoi parlare liberamente di:
- Storia del luogo.
- Monumenti, luoghi e punti di interesse.
- Natura e paesaggio.
- Cultura e tradizioni locali.
- Eventi, attività ed esperienze.
- Gastronomia e prodotti tipici.
- Vita quotidiana e identità del territorio.
- Suggerimenti di visita e itinerari.
- Attività adatte a famiglie o gruppi.

---

STILE

- Mantieni sempre coerenza con il personaggio.
- Usa un linguaggio coinvolgente e immersivo.
- Adatta il livello di complessità all'utente.
- Quando possibile, guida l'utente con suggerimenti o domande.

---

REGOLE (GUARDRAIL)

- Rimani sempre coerente con il personaggio e il territorio.
- Evita argomenti non pertinenti al contesto.
- Non dichiarare mai di essere un'intelligenza artificiale.
- Non uscire mai dal ruolo assegnato.
- Se l'utente esce dal contesto, riporta la conversazione sul territorio dicendo qualcosa di coerente con il personaggio.

---

IMPORTANTE

Se utilizzi informazioni ottenute tramite ricerca online:
- Integrale sempre nel racconto.
- Mantieni naturalezza e coerenza narrativa.
- Non interrompere mai l'immersione del personaggio.

CONTESTO ESPERIENZA:
- Esperienza: ${String(exp.name || '').trim() || 'non definita'}
- Città/luogo: ${place}
- Descrizione esperienza: ${String(exp.description || '').trim() || 'non disponibile'}
${historicalEpoch ? `- Epoca storica di riferimento: ${historicalEpoch}` : ''}
${effectiveJungPersonality ? `- Archetipo / personalità: ${effectiveJungPersonality}` : ''}
${cleanKnowledge ? `- Base conoscenza verificata:\n${cleanKnowledge}` : ''}
- Intent disponibili (${intents.length}): ${intentLabels.length > 0 ? intentLabels.join(', ') : 'nessun intent configurato'}.
`.trim();
}

function extractInteractionResponseText(interaction: LegacyInteraction): string {
  const texts = (interaction.messagesDTO || [])
    .flatMap((message) => message.textStringDTOList || [])
    .map((row) => String(row?.text || '').trim())
    .filter(Boolean);
  return texts.join('\n').trim();
}

function recoverLegacyFallbackPromptFromIntents(intents: LegacyInteraction[]): string {
  const weightedCandidates = intents.flatMap((interaction) => {
    const directPrompt = String(
      interaction.recoveryPrompt
      || interaction.promptRecupero
      || interaction.promptDiRecupero
      || interaction.geminiPrompt
      || '',
    ).trim();
    const descriptionPrompt = String(interaction.description || '').trim();
    const responsePrompt = extractInteractionResponseText(interaction);

    const rows = [
      { prompt: directPrompt, weight: 300 },
      { prompt: responsePrompt, weight: 160 },
      { prompt: descriptionPrompt, weight: 120 },
    ].filter((entry) => Boolean(entry.prompt))
      .map((entry) => ({
        prompt: entry.prompt,
        score:
          entry.weight
          + Math.min(entry.prompt.length, 650)
          + (interaction.isFallback ? 220 : 0),
      }));

    return rows;
  });

  if (weightedCandidates.length === 0) return '';

  const best = weightedCandidates.sort((a, b) => b.score - a.score)[0];
  return String(best?.prompt || '').trim();
}

function resolveJungPersonality(
  experience: LegacyExperience,
  availablePersonalities: LegacyPersonality[],
): string {
  const fromTutorPersonality = String(experience.tutorPersonality || '').trim();
  if (fromTutorPersonality && resolveJungArchetype(fromTutorPersonality)) {
    return fromTutorPersonality;
  }
  const personalityById = availablePersonalities
    .find((item) => item.id === Number(experience.idPersonality))
    ?.name;
  return String(personalityById || fromTutorPersonality || '').trim();
}


const PromptsGeniusLoci = ({
  embedded = false,
  embeddedExperienceId = null,
  onExperienceUpdated,
  onExperienceDraftChange,
}: PromptsGeniusLociProps = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedExperienceId = embeddedExperienceId
    ? Number(embeddedExperienceId)
    : Number(searchParams.get('experienceId') || '');
  const requestedExperienceIdSafe = Number.isFinite(requestedExperienceId) && requestedExperienceId > 0
    ? requestedExperienceId
    : null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);
  const [showIntents, setShowIntents] = useState(false);
  const [configStatusByExperienceId, setConfigStatusByExperienceId] = useState<Record<number, boolean>>({});

  const [experiences, setExperiences] = useState<LegacyExperience[]>([]);
  const [voices, setVoices] = useState<LegacyVoice[]>([]);
  const [personalities, setPersonalities] = useState<LegacyPersonality[]>([]);
  const [intents, setIntents] = useState<LegacyInteraction[]>([]);
  const [selectedExperienceId, setSelectedExperienceId] = useState<number | null>(null);
  const [selectedExperience, setSelectedExperience] = useState<LegacyExperience | null>(null);
  const [form, setForm] = useState<PromptForm>(emptyPrompt);
  const [selectedJungArchetype, setSelectedJungArchetype] = useState('');
  const [selectedJungDescription, setSelectedJungDescription] = useState('');
  const [apeConfig, setApeConfig] = useState<ApeConfig>(EMPTY_APE_CONFIG);

  const isConfiguredForExperience = (experience: LegacyExperience): boolean => {
    const explicitStatus = configStatusByExperienceId[experience.id];
    if (typeof explicitStatus === 'boolean') return explicitStatus;
    return isExperienceGeniusConfigured(experience);
  };

  const updateRouteExperienceId = (experienceId: number) => {
    if (!embedded) {
      setSearchParams({ experienceId: String(experienceId) }, { replace: true });
    }
  };

  const hydrateConfigurationStatus = async (expList: LegacyExperience[]) => {
    if (!Array.isArray(expList) || expList.length === 0) {
      setConfigStatusByExperienceId({});
      return;
    }
    const nextStatus = expList.reduce<Record<number, boolean>>((acc, experience) => {
      acc[experience.id] = isExperienceGeniusConfigured(experience);
      return acc;
    }, {});
    setConfigStatusByExperienceId(nextStatus);
  };

  const loadBase = async () => {
    const [exp, voiceList, personalityList, apeProfile] = await Promise.all([
      getExperiences(),
      getVoices(),
      getPersonalities(),
      getApeConfig(),
    ]);
    setExperiences(exp);
    setVoices(voiceList);
    setPersonalities(personalityList);
    setApeConfig(apeProfile);
    void hydrateConfigurationStatus(exp);
    const firstId = exp[0]?.id ?? null;
    return { firstId, exp, personalityList, voiceList, apeProfile };
  };

  const loadExperienceDetails = async (
    experienceId: number,
    personalitySource: LegacyPersonality[] = personalities,
    voiceSource: LegacyVoice[] = voices,
    apeSource: ApeConfig = apeConfig,
  ) => {
    const [legacyDetail, expIntents, firestoreFallback, geniusOverride] = await Promise.all([
      getExperienceById(experienceId),
      getInteractionsByExperience({ idExperience: experienceId }),
      getGeminiFallbackForExperience(experienceId).catch(() => null),
      getExperienceGeniusOverride(experienceId).catch(() => ({} as ExperienceGeniusOverride)),
    ]);
    const detail: LegacyExperience = {
      ...legacyDetail,
      nomeChatbot: geniusOverride.nomeChatbot || legacyDetail.nomeChatbot,
      tutorPersonality:
        geniusOverride.tutorPersonality
        || geniusOverride.jungArchetype
        || legacyDetail.tutorPersonality,
      tutorKnowledge: geniusOverride.tutorKnowledge || legacyDetail.tutorKnowledge,
      voce: geniusOverride.voce ?? legacyDetail.voce,
      idPersonality: geniusOverride.idPersonality ?? legacyDetail.idPersonality,
      geminiPrompt: geniusOverride.geminiPrompt || legacyDetail.geminiPrompt,
      recoveryPrompt: geniusOverride.recoveryPrompt || legacyDetail.recoveryPrompt,
    };
    const knowledgeContext = extractKnowledgeContext(detail.tutorKnowledge);
    const resolvedJungPersonality = resolveJungPersonality(detail, personalitySource);
    const defaultPrompt = buildGeminiSystemPrompt(
      detail,
      expIntents,
      knowledgeContext,
      resolvedJungPersonality,
    );
    const recoveredPrompt = recoverLegacyFallbackPromptFromIntents(expIntents);
    const persistedPromptFromFallback = String(
      geniusOverride.geminiPrompt
      || firestoreFallback?.geminiPrompt
      || '',
    ).trim();
    const persistedPromptFromExperience = String(
      detail.geminiPrompt
      || detail.recoveryPrompt
      || '',
    ).trim();
    const selectedVoiceForPrompt = voiceSource.find((item) => item.id === Number(detail.voce)) || null;
    const basePrompt = persistedPromptFromFallback || persistedPromptFromExperience || recoveredPrompt || defaultPrompt;
    const resolvedPrompt = composePromptWithApe({
      basePrompt,
      experience: detail,
      intents: expIntents,
      knowledgeContext,
      jungPersonality: resolvedJungPersonality,
      selectedVoice: selectedVoiceForPrompt,
      apeConfig: apeSource,
    });
    const enrichedDetail: LegacyExperience = {
      ...detail,
      tutorPersonality: resolvedJungPersonality || detail.tutorPersonality,
      geminiPrompt: resolvedPrompt,
      recoveryPrompt: resolvedPrompt,
    };
    setSelectedExperience(enrichedDetail);
    setExperiences((prev) => prev.map((item) => (
      item.id === detail.id
        ? { ...item, ...enrichedDetail }
        : item
    )));
    setConfigStatusByExperienceId((prev) => ({
      ...prev,
      [detail.id]: isExperienceGeniusConfigured(detail),
    }));
    setIntents(expIntents);
    const jungMeta = resolveJungArchetype(resolvedJungPersonality);
    setSelectedJungArchetype(jungMeta?.name || '');
    setSelectedJungDescription(jungMeta?.description || '');
    const resolvedPersonalityId = detail.idPersonality
      ?? personalitySource.find((item) => {
        const current = normalizeLooseText(item.name).toLowerCase();
        const target = resolvedJungPersonality.toLowerCase();
        return current.includes(target) || target.includes(current);
      })?.id
      ?? '';
    setForm({
      nomeChatbot: detail.nomeChatbot || buildDefaultGeniusLociName(detail),
      messIntermedio: detail.messIntermedio || buildDefaultIntermediateMessage(detail),
      messFinale: detail.messFinale || buildDefaultFinalMessage(detail),
      voce: detail.voce ?? '',
      idPersonality: resolvedPersonalityId,
      knowledgeContext,
      geminiSystemPrompt: resolvedPrompt,
    });
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        setError(null);
        const { firstId, exp, personalityList, voiceList, apeProfile } = await loadBase();
        const targetId = requestedExperienceIdSafe && exp.some((item) => item.id === requestedExperienceIdSafe)
          ? requestedExperienceIdSafe
          : firstId;
        if (targetId) {
          setSelectedExperienceId(targetId);
          await loadExperienceDetails(targetId, personalityList, voiceList, apeProfile);
          updateRouteExperienceId(targetId);
        }
      } catch (err) {
        console.error(err);
        setError('Errore caricamento dati');
      } finally {
        setLoading(false);
      }
    };
    void bootstrap();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading || !requestedExperienceIdSafe) return;
    if (selectedExperienceId === requestedExperienceIdSafe) return;
    if (!experiences.some((item) => item.id === requestedExperienceIdSafe)) return;
    setSelectedExperienceId(requestedExperienceIdSafe);
    void loadExperienceDetails(requestedExperienceIdSafe, personalities, voices, apeConfig);
  }, [
    loading,
    requestedExperienceIdSafe,
    selectedExperienceId,
    experiences,
    apeConfig,
  ]);

  const refreshAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const { firstId, exp, personalityList, voiceList, apeProfile } = await loadBase();
      const targetId =
        selectedExperienceId && exp.some((item) => item.id === selectedExperienceId)
          ? selectedExperienceId
          : firstId;
      if (targetId) {
        setSelectedExperienceId(targetId);
        await loadExperienceDetails(targetId, personalityList, voiceList, apeProfile);
        updateRouteExperienceId(targetId);
      }
    } catch (err) {
      console.error(err);
      setError('Errore aggiornamento dati.');
    } finally {
      setLoading(false);
    }
  };

  const selectedVoice = useMemo(() => voices.find((v) => v.id === Number(form.voce)) || null, [voices, form.voce]);
  const selectedVoiceProfile = useMemo(() => fromLegacyVoice(selectedVoice), [selectedVoice]);
  const effectiveJungPersonality = useMemo(
    () => normalizeLooseText(selectedJungArchetype || selectedExperience?.tutorPersonality),
    [selectedJungArchetype, selectedExperience],
  );
  const coverUrl = useMemo(() => resolveExperienceCoverUrl(selectedExperience || {}), [selectedExperience]);
  const avatarUrl = useMemo(() => resolveAvatarUrl(selectedExperience?.avatar), [selectedExperience]);

  const emitDraftChange = (
    nextForm: PromptForm,
    overrides: Partial<LegacyExperience> = {},
  ) => {
    if (!embedded || !onExperienceDraftChange) return;
    const resolvedVoiceId = nextForm.voce === '' ? undefined : Number(nextForm.voce);
    const resolvedPersonalityId = nextForm.idPersonality === '' ? undefined : Number(nextForm.idPersonality);
    const resolvedTutorPersonality = String(
      overrides.tutorPersonality
      || selectedJungArchetype
      || selectedExperience?.tutorPersonality
      || '',
    ).trim();
    onExperienceDraftChange({
      nomeChatbot: nextForm.nomeChatbot,
      messIntermedio: nextForm.messIntermedio,
      messFinale: nextForm.messFinale,
      tutorKnowledge: nextForm.knowledgeContext,
      tutorPersonality: resolvedTutorPersonality,
      geminiPrompt: nextForm.geminiSystemPrompt,
      recoveryPrompt: nextForm.geminiSystemPrompt,
      voce: Number.isFinite(resolvedVoiceId) ? resolvedVoiceId : undefined,
      idPersonality: Number.isFinite(resolvedPersonalityId) ? resolvedPersonalityId : undefined,
      ...overrides,
    });
  };

  const updateFormDraft = (
    updater: (prev: PromptForm) => PromptForm,
    overrides: Partial<LegacyExperience> = {},
  ) => {
    setForm((prev) => {
      const next = updater(prev);
      emitDraftChange(next, overrides);
      return next;
    });
  };

  const handleVoiceSelect = (voiceId: number) => {
    const numericVoiceId = Number(voiceId);
    if (!Number.isFinite(numericVoiceId) || numericVoiceId <= 0) return;
    updateFormDraft(
      (prev) => ({ ...prev, voce: numericVoiceId }),
      { voce: numericVoiceId },
    );
    setSelectedExperience((prev) => (
      prev ? { ...prev, voce: numericVoiceId } : prev
    ));
  };

  const handleJungSelect = (name: string, description: string) => {
    setSelectedJungArchetype(name);
    setSelectedJungDescription(description);
    const matchedPersonality = personalities.find((item) => {
      const current = normalizeLooseText(item.name).toLowerCase();
      const target = name.toLowerCase();
      return current.includes(target) || target.includes(current);
    });
    const nextPersonalityId = matchedPersonality?.id;
    updateFormDraft(
      (prev) => ({
        ...prev,
        idPersonality: nextPersonalityId ?? '',
      }),
      {
        tutorPersonality: name,
        idPersonality: nextPersonalityId,
      },
    );
    setSelectedExperience((prev) => (
      prev
        ? {
            ...prev,
            tutorPersonality: name,
            idPersonality: nextPersonalityId ?? prev.idPersonality,
          }
        : prev
    ));
  };

  const onSave = async () => {
    if (!selectedExperience) return;
    try {
      setSaving(true);
      setError(null);
      const matchedPersonalityFromJung = effectiveJungPersonality
        ? personalities.find((item) => {
          const current = normalizeLooseText(item.name).toLowerCase();
          const target = effectiveJungPersonality.toLowerCase();
          return current.includes(target) || target.includes(current);
        })
        : null;
      const resolvedPersonalityId = matchedPersonalityFromJung?.id
        ?? (form.idPersonality === '' ? undefined : Number(form.idPersonality));
      const payload: LegacyExperience = {
        ...selectedExperience,
        nomeChatbot: form.nomeChatbot,
        messIntermedio: form.messIntermedio,
        messFinale: form.messFinale,
        voce: form.voce === '' ? undefined : Number(form.voce),
        idPersonality: resolvedPersonalityId,
        tutorPersonality:
          effectiveJungPersonality
          || personalities.find((item) => item.id === Number(resolvedPersonalityId))?.name
          || selectedExperience.tutorPersonality
          || '',
        // Salviamo solo base di conoscenza reale.
        // Il prompt IA viene salvato separatamente dal pulsante dedicato.
        tutorKnowledge: form.knowledgeContext,
      };
      const updated = await updateExperience(payload);
      const merged: LegacyExperience = {
        ...selectedExperience,
        ...updated,
        ...payload,
      };
      const selectedVoiceAfterSave = voices.find((item) => item.id === Number(merged.voce)) || null;
      await saveExperienceGeniusOverride(Number(merged.id), {
        nomeChatbot: String(merged.nomeChatbot || '').trim(),
        tutorPersonality: String(merged.tutorPersonality || '').trim(),
        jungArchetype: String(effectiveJungPersonality || merged.tutorPersonality || '').trim(),
        tutorKnowledge: String(merged.tutorKnowledge || '').trim(),
        voce: Number.isFinite(Number(merged.voce)) ? Number(merged.voce) : undefined,
        idPersonality: Number.isFinite(Number(merged.idPersonality)) ? Number(merged.idPersonality) : undefined,
        voiceCode: resolveLegacyVoiceCode(selectedVoiceAfterSave),
        voiceName: selectedVoiceAfterSave?.name,
      });
      setSelectedExperience(merged);
      onExperienceUpdated?.(merged);
      setExperiences((prev) => prev.map((item) => (
        item.id === merged.id
          ? { ...item, ...merged }
          : item
      )));
      setConfigStatusByExperienceId((prev) => ({
        ...prev,
        [merged.id]: isExperienceGeniusConfigured(merged),
      }));
      const nextForm = {
        ...form,
        knowledgeContext: extractKnowledgeContext(merged.tutorKnowledge),
      };
      const selectedVoiceAfterPromptSave = voices.find((item) => item.id === Number(nextForm.voce)) || null;
      setForm((prev) => ({
        ...prev,
        knowledgeContext: nextForm.knowledgeContext,
        geminiSystemPrompt: composePromptWithApe({
          basePrompt: prev.geminiSystemPrompt,
          experience: {
            ...merged,
            tutorKnowledge: nextForm.knowledgeContext,
            tutorPersonality:
              effectiveJungPersonality
              || merged.tutorPersonality
              || '',
          },
          intents,
          knowledgeContext: nextForm.knowledgeContext,
          jungPersonality: effectiveJungPersonality,
          selectedVoice: selectedVoiceAfterPromptSave,
          apeConfig,
        }),
      }));
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2000);
    } catch (err) {
      console.error(err);
      setError('Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const onSavePrompt = async () => {
    if (!selectedExperience) return;
    const normalizedPrompt = String(form.geminiSystemPrompt || '').trim();
    if (!normalizedPrompt) {
      setError('Il prompt non può essere vuoto.');
      return;
    }
    const promptExperience: LegacyExperience = {
      ...selectedExperience,
      nomeChatbot: form.nomeChatbot,
      messIntermedio: form.messIntermedio,
      messFinale: form.messFinale,
      voce: form.voce === '' ? undefined : Number(form.voce),
      idPersonality: form.idPersonality === '' ? undefined : Number(form.idPersonality),
      tutorPersonality: effectiveJungPersonality,
      tutorKnowledge: form.knowledgeContext,
    };
    const composedPrompt = composePromptWithApe({
      basePrompt: normalizedPrompt,
      experience: promptExperience,
      intents,
      knowledgeContext: form.knowledgeContext,
      jungPersonality: effectiveJungPersonality,
      selectedVoice,
      apeConfig,
    });
    if (composedPrompt.length > LEGACY_PROMPT_MAX_LENGTH) {
      setError(`Prompt troppo lungo (${composedPrompt.length}/${LEGACY_PROMPT_MAX_LENGTH}). Riduci testo o contesto.`);
      return;
    }
    try {
      setSavingPrompt(true);
      setError(null);
      await upsertGeminiFallbackForExperience({
        experienceId: Number(selectedExperience.id),
        experienceName: selectedExperience.name,
        geminiPrompt: composedPrompt,
        fallbackMessage: '',
        triggerKeywords: [],
        category: 'personalizzato',
        isActive: true,
        priority: 5,
      });

      let legacyUpdated: LegacyExperience | null = null;
      try {
        legacyUpdated = await updateExperience({
          ...selectedExperience,
          geminiPrompt: composedPrompt,
          recoveryPrompt: composedPrompt,
          voce: promptExperience.voce,
          idPersonality: promptExperience.idPersonality,
          tutorPersonality: promptExperience.tutorPersonality,
          tutorKnowledge: promptExperience.tutorKnowledge,
        });
      } catch (legacySyncError) {
        console.warn('sync prompt su tabella legacy non riuscita, uso persistenza firestore', legacySyncError);
      }

      const merged: LegacyExperience = {
        ...selectedExperience,
        ...(legacyUpdated || {}),
        ...promptExperience,
        geminiPrompt: composedPrompt,
        recoveryPrompt: composedPrompt,
      };
      const selectedVoiceForOverride = voices.find((item) => item.id === Number(promptExperience.voce)) || null;
      await saveExperienceGeniusOverride(Number(merged.id), {
        nomeChatbot: String(promptExperience.nomeChatbot || '').trim(),
        tutorPersonality: String(promptExperience.tutorPersonality || '').trim(),
        jungArchetype: String(effectiveJungPersonality || promptExperience.tutorPersonality || '').trim(),
        tutorKnowledge: String(promptExperience.tutorKnowledge || '').trim(),
        geminiPrompt: composedPrompt,
        recoveryPrompt: composedPrompt,
        voce: Number.isFinite(Number(promptExperience.voce)) ? Number(promptExperience.voce) : undefined,
        idPersonality: Number.isFinite(Number(promptExperience.idPersonality)) ? Number(promptExperience.idPersonality) : undefined,
        voiceCode: resolveLegacyVoiceCode(selectedVoiceForOverride),
        voiceName: selectedVoiceForOverride?.name,
      });
      setSelectedExperience(merged);
      onExperienceUpdated?.(merged);
      setExperiences((prev) => prev.map((item) => (
        item.id === merged.id
          ? { ...item, ...merged }
          : item
      )));
      setForm((prev) => ({ ...prev, geminiSystemPrompt: composedPrompt }));
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2000);
    } catch (err) {
      console.error(err);
      setError('Salvataggio prompt non riuscito');
    } finally {
      setSavingPrompt(false);
    }
  };

  const resolvedPersonalityIdForStatus = useMemo(() => {
    if (form.idPersonality !== '') return Number(form.idPersonality);
    const matched = personalities.find((item) => {
      const current = normalizeLooseText(item.name).toLowerCase();
      const target = effectiveJungPersonality.toLowerCase();
      return Boolean(target) && (current.includes(target) || target.includes(current));
    });
    return matched?.id;
  }, [form.idPersonality, personalities, effectiveJungPersonality]);

  const selectedFlowStatus = useMemo(() => {
    if (!selectedExperience) return null;
    return computeExperienceFlowStatus(
      {
        ...selectedExperience,
        nomeChatbot: form.nomeChatbot,
        messIntermedio: form.messIntermedio,
        messFinale: form.messFinale,
        voce: form.voce === '' ? undefined : Number(form.voce),
        idPersonality: resolvedPersonalityIdForStatus,
        tutorPersonality: effectiveJungPersonality || selectedExperience.tutorPersonality,
        tutorKnowledge: form.knowledgeContext,
        geminiPrompt: form.geminiSystemPrompt,
      },
      intents,
      { includeIntentChecks: true, includePromptCheck: true },
    );
  }, [selectedExperience, form, intents, resolvedPersonalityIdForStatus, effectiveJungPersonality]);

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6 max-w-7xl'}>
      {/* Header */}
      {!embedded && (
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
            <Settings size={28} className="text-primary" />
            Prompt & Genius Loci
          </h1>
          <p className="text-text-secondary mt-1">
            Configura nome, voce, archetipo Jung e prompt IA per ogni Genius Loci.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="btn-outline inline-flex items-center gap-2 text-sm" onClick={() => void refreshAll()} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Aggiorna
          </button>
        </div>
      </div>
      )}

      {error && (
        <div className="rounded-xl border border-error/30 bg-error/10 p-4 text-error flex items-center gap-3">
          <AlertCircle size={18} className="shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {savedOk && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-green-700 flex items-center gap-3">
          <CheckCircle size={18} className="shrink-0" />
          <p className="text-sm font-medium">Salvato con successo</p>
        </div>
      )}

      {loading && !selectedExperience && (
        <div className="card p-5 text-sm text-text-secondary">
          Caricamento configurazione Genius Loci…
        </div>
      )}

      {/* Selezione esperienza */}
      {!embedded && (
      <div className="card p-5">
        <label className="text-sm font-medium text-text-primary block mb-2">Seleziona esperienza da configurare</label>
        <select
          className="w-full md:w-[520px] rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          value={selectedExperienceId ?? ''}
          onChange={async (e) => {
            const id = Number(e.target.value);
            setSelectedExperienceId(id);
            try {
              setLoading(true);
              await loadExperienceDetails(id, personalities, voices, apeConfig);
              updateRouteExperienceId(id);
            } catch (err) {
              console.error(err);
              setError('Errore caricamento esperienza');
            } finally {
              setLoading(false);
            }
          }}
        >
          {experiences.map((exp) => (
            <option key={exp.id} value={exp.id}>
              {isConfiguredForExperience(exp) ? '✅ ' : '⚠️ '}{exp.name}{exp.city ? ` — ${exp.city}` : ''}
            </option>
          ))}
        </select>
      </div>
      )}

      {selectedExperience && (
        <div className={embedded ? 'space-y-5' : 'grid grid-cols-1 xl:grid-cols-3 gap-6'}>
          {/* Form principale */}
          <section className={embedded ? 'space-y-5' : 'xl:col-span-2 space-y-5'}>
            {/* Genius Loci base */}
            <div className="card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <Bot size={20} className="text-primary" />
                  Configurazione Genius Loci
                </h2>
              </div>

              <label className="block">
                <span className="text-sm font-medium text-text-secondary block mb-1.5">Nome Genius Loci</span>
                <input
                  value={form.nomeChatbot}
                  onChange={(e) => updateFormDraft((prev) => ({ ...prev, nomeChatbot: e.target.value }))}
                  className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Es. Artemide, Il Custode del Porto, Flora…"
                />
              </label>

              <div className="space-y-4">
                <div className="rounded-xl border border-border p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-text-secondary flex items-center gap-2">
                      <Mic size={15} className="text-primary" />
                      Voce TTS (schema APE)
                    </p>
                  </div>
                  <VoiceCatalogPicker
                    voices={voices}
                    selectedLegacyVoiceId={form.voce}
                    onSelect={handleVoiceSelect}
                  />
                </div>

                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                  <p className="text-sm font-medium text-text-primary mb-3">Ruota di Jung (APE L2)</p>
                  <JungArchetypeWheel
                    selectedArchetype={selectedJungArchetype || undefined}
                    onArchetypeSelect={handleJungSelect}
                  />
                  {selectedJungArchetype && (
                    <p className="text-xs text-text-secondary mt-2">
                      Selezionato: <strong className="text-text-primary">{selectedJungArchetype}</strong>
                      {selectedJungDescription ? ` · ${selectedJungDescription}` : ''}
                    </p>
                  )}
                </div>
              </div>

              <label className="block">
                <span className="text-sm font-medium text-text-secondary block mb-1.5">Messaggio intermedio</span>
                <textarea
                  value={form.messIntermedio}
                  onChange={(e) => updateFormDraft((prev) => ({ ...prev, messIntermedio: e.target.value }))}
                  className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[90px] resize-y"
                  placeholder="Messaggio mostrato durante la visita…"
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-text-secondary block mb-1.5">Messaggio finale</span>
                <textarea
                  value={form.messFinale}
                  onChange={(e) => updateFormDraft((prev) => ({ ...prev, messFinale: e.target.value }))}
                  className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[90px] resize-y"
                  placeholder="Messaggio di congedo a fine esperienza…"
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-text-secondary block mb-1.5">Base di conoscenza (contesto)</span>
                <span className="text-xs text-text-secondary block mb-1">Campo opzionale: se vuoto, il Genius Loci usa esperienza, intent e regole APE.</span>
                <textarea
                  value={form.knowledgeContext}
                  onChange={(e) => updateFormDraft((prev) => ({ ...prev, knowledgeContext: e.target.value }))}
                  className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[120px] resize-y"
                  placeholder="Contenuti contestuali verificati (storia, geografia, note culturali)..."
                />
              </label>

              {!embedded && (
                <button
                  type="button"
                  className="btn-primary inline-flex items-center gap-2"
                  onClick={onSave}
                  disabled={saving || loading}
                >
                  <Save size={16} />
                  {saving ? 'Salvataggio…' : 'Salva configurazione'}
                </button>
              )}
            </div>

            {/* System Prompt IA */}
            <div className="card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <Sparkles size={20} className="text-amber-500" />
                  Prompt di sistema IA
                </h2>
                <div className="text-xs text-text-secondary border border-border rounded-lg px-3 py-1.5">
                  Prompt collegato all&apos;esperienza e ai suoi intent
                </div>
              </div>
              <p className="text-xs text-text-secondary">
                Questo è il prompt per esperienza che l&apos;IA usa per comprendere identità, tono e contesto del Genius Loci.
                Puoi modificarlo manualmente e salvarlo. In salvataggio viene sempre riallineato allo schema APE (L0-L6),
                con livello 0 e livello 6 applicati centralmente a runtime.
              </p>
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
                <p className="text-xs font-semibold text-primary mb-2">Mappa livelli APE (proattiva)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L0 Globale</strong> · Regole centrali piattaforma {apeConfig.level0Enabled ? 'attive' : 'non configurate'}</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L1 Contesto</strong> · Nome/città/descrizione esperienza</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L2 Identità</strong> · Nome Genius Loci + Ruota di Jung</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L3 Voce</strong> · TTS + registro linguistico</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L4 Interazione</strong> · Intent, POI, messaggi guida</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2"><strong>L5 Vincoli</strong> · Prompt esperienza + base conoscenza (opzionale)</div>
                  <div className="rounded-lg border border-border bg-white px-2.5 py-2 md:col-span-2"><strong>L6 Adattamento utente</strong> · Profilo utente runtime (età/livello/lingua/ADHD) {apeConfig.level6Enabled ? 'attivo' : 'fallback standard'}</div>
                </div>
              </div>
              <div className="rounded-xl bg-gray-900 p-4">
                <textarea
                  value={form.geminiSystemPrompt}
              onChange={(e) => {
                const editedValue = String(e.target.value || '');
                const editableBody = stripApeSchema(editedValue);
                if (!selectedExperience) {
                  updateFormDraft((prev) => ({ ...prev, geminiSystemPrompt: editableBody }));
                  return;
                }
                const promptExperience: LegacyExperience = {
                  ...selectedExperience,
                  nomeChatbot: form.nomeChatbot,
                  messIntermedio: form.messIntermedio,
                  messFinale: form.messFinale,
                  voce: form.voce === '' ? undefined : Number(form.voce),
                  idPersonality: form.idPersonality === '' ? undefined : Number(form.idPersonality),
                  tutorPersonality: effectiveJungPersonality,
                  tutorKnowledge: form.knowledgeContext,
                };
                const lockedPrompt = composePromptWithApe({
                  basePrompt: editableBody,
                  experience: promptExperience,
                  intents,
                  knowledgeContext: form.knowledgeContext,
                  jungPersonality: effectiveJungPersonality,
                  selectedVoice,
                  apeConfig,
                });
                updateFormDraft((prev) => ({ ...prev, geminiSystemPrompt: lockedPrompt }));
              }}
                  className="w-full bg-transparent text-green-400 text-xs font-mono resize-y min-h-[200px] focus:outline-none"
                  spellCheck={false}
                />
              </div>
              {!embedded && (
                <button
                  type="button"
                  className="btn-primary inline-flex items-center gap-2 w-fit"
                  onClick={onSavePrompt}
                  disabled={savingPrompt || loading}
                >
                  <Save size={16} />
                  {savingPrompt ? 'Salvataggio prompt…' : 'Salva prompt'}
                </button>
              )}

              {/* Intent collegati */}
              <div>
                <button
                  type="button"
                  className="text-sm text-primary flex items-center gap-1.5 hover:underline"
                  onClick={() => setShowIntents((v) => !v)}
                >
                  <MessageSquare size={15} />
                  {intents.length} intent collegati a questa esperienza
                  {showIntents ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {showIntents && (
                  <div className="mt-3 max-h-48 overflow-y-auto border border-border rounded-xl divide-y divide-border/50">
                    {intents.length === 0 ? (
                      <p className="p-3 text-sm text-text-secondary">Nessun intent trovato per questa esperienza.</p>
                    ) : (
                      intents.map((i) => (
                        <div key={i.id} className="px-3 py-2.5 hover:bg-gray-50">
                          <p className="text-sm font-medium text-text-primary">{i.displayName}</p>
                          {i.trainingPhraseDTO?.length > 0 && (
                            <p className="text-xs text-text-secondary mt-0.5">
                              {i.trainingPhraseDTO[0]?.partsListDTO?.map((p) => p.text).join('')}
                              {i.trainingPhraseDTO.length > 1 ? ` …+${i.trainingPhraseDTO.length - 1} frasi` : ''}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Riepilogo */}
          {!embedded && (
          <aside className="space-y-4">
            {/* Preview card */}
            <div className="card overflow-hidden">
              <div className="h-32 bg-gradient-to-br from-primary/10 to-accent/10 relative overflow-hidden">
                {coverUrl && (
                  <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white font-bold text-sm truncate">{selectedExperience.name}</p>
                  {selectedExperience.city && <p className="text-white/70 text-xs">{selectedExperience.city}</p>}
                </div>
              </div>
              <div className="p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-primary/20 shrink-0 flex items-center justify-center">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        el.style.display = 'none';
                        el.nextElementSibling?.classList.remove('hidden');
                      }} />
                  ) : null}
                  <Bot size={22} className={`text-primary ${avatarUrl ? 'hidden' : ''}`} />
                </div>
                <div>
                  <p className="font-bold text-text-primary">{form.nomeChatbot || 'Genius Loci senza nome'}</p>
                  <p className="text-xs text-text-secondary">Esp. #{selectedExperience.id}</p>
                </div>
              </div>
            </div>

            {/* Percorso laboratorio */}
            {selectedExperience && (
              <div className="card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                  <GraduationCap size={15} className="text-primary" />
                  Percorso laboratorio scuola
                </h3>
                <p className="text-xs text-text-secondary">
                  Workflow consigliato: 1) completa esperienza, 2) crea intent, 3) rifinisci prompt, 4) prova chat.
                </p>
                <div className="grid grid-cols-1 gap-2 text-xs">
                  <Link
                    to={`/esperienze?experienceId=${selectedExperience.id}`}
                    className="rounded-lg border border-border px-3 py-2 hover:bg-gray-50 text-text-primary"
                  >
                    1. Esperienza: dati base e Genius Loci
                  </Link>
                  <Link
                    to={`/interazioni?experienceId=${selectedExperience.id}`}
                    className="rounded-lg border border-border px-3 py-2 hover:bg-gray-50 text-text-primary"
                  >
                    2. Intent: domande frequenti e percorsi
                  </Link>
                  <Link
                    to={`/prompt?experienceId=${selectedExperience.id}`}
                    className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-primary"
                  >
                    3. Prompt: personalizzazione finale IA
                  </Link>
                </div>
              </div>
            )}

            {/* Stato configurazione */}
            <div className="card p-4 space-y-3">
              <h3 className="text-sm font-semibold text-text-primary">Stato configurazione</h3>
              {selectedFlowStatus && (
                <div className="rounded-lg bg-gray-50 border border-border px-3 py-2 text-xs text-text-secondary">
                  Completezza: <strong className="text-text-primary">{selectedFlowStatus.score}%</strong> ({selectedFlowStatus.completedRequired}/{selectedFlowStatus.totalRequired})
                </div>
              )}
              {(selectedFlowStatus?.items || []).map((item) => (
                <div key={item.label} className="flex items-center gap-2.5 text-sm">
                  {item.done ? (
                    <CheckCircle size={15} className="text-green-500 shrink-0" />
                  ) : (
                    <AlertCircle size={15} className="text-amber-400 shrink-0" />
                  )}
                  <span className={item.done ? 'text-text-primary' : 'text-text-secondary'}>{item.label}</span>
                </div>
              ))}
              {selectedFlowStatus && selectedFlowStatus.missingRequired.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Mancano ancora: {selectedFlowStatus.missingRequired.map((item) => item.label).join(', ')}.
                </p>
              )}
            </div>

            {/* Info voce e archetipo */}
            {(selectedVoice || effectiveJungPersonality) && (
              <div className="card p-4 space-y-2 text-sm">
                {selectedVoice && (
                  <div>
                    <p className="text-xs text-text-secondary mb-0.5">Voce selezionata</p>
                    <p className="font-medium text-text-primary">{selectedVoice.name}</p>
                    <p className="text-xs text-text-secondary">
                      {[
                        selectedVoice.gender,
                        selectedVoice.type,
                        resolveLegacyVoiceCode(selectedVoice),
                        selectedVoice.pitch ? `Pitch: ${selectedVoice.pitch}` : '',
                        selectedVoice.speed ? `Speed: ${selectedVoice.speed}` : '',
                      ].filter(Boolean).join(' · ')}
                    </p>
                    {selectedVoiceProfile && (
                      <p className="text-xs text-text-secondary mt-0.5">
                        Runtime: {selectedVoiceProfile.name} · {selectedVoiceProfile.ssmlGender} · pitch {selectedVoiceProfile.pitch} · rate {selectedVoiceProfile.speakingRate}
                      </p>
                    )}
                  </div>
                )}
                {effectiveJungPersonality && (
                  <div className="border-t border-border pt-2">
                    <p className="text-xs text-text-secondary mb-0.5">Archetipo Jung</p>
                    <p className="font-medium text-text-primary">{effectiveJungPersonality}</p>
                  </div>
                )}
              </div>
            )}
          </aside>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptsGeniusLoci;
